export type ChatMapLiveSlotId = string

export type ChatMapLiveSlotListener = () => void

type RankedEntry = {
  /** Monotonic claim/visibility order — higher means more recently relevant. */
  rank: number
}

/**
 * Coordinates how many in-chat Mapbox maps may be live at once.
 *
 * Cards report visibility independently (IntersectionObserver). Leaving the
 * viewport does **not** tear down a live Mapbox instance — that avoids reload
 * flicker when scrolling a single card away and back. Slots are only taken
 * from off-screen (or least-recent) holders when a newly visible card needs
 * capacity.
 */
export class ChatMapLiveSlotRegistry {
  private maxSlots: number
  private nextRank = 1
  private readonly visibleById = new Map<ChatMapLiveSlotId, RankedEntry>()
  private readonly liveById = new Map<ChatMapLiveSlotId, RankedEntry>()
  private readonly listeners = new Set<ChatMapLiveSlotListener>()

  constructor(maxSlots: number) {
    this.maxSlots = Math.max(1, maxSlots)
  }

  getMaxSlots(): number {
    return this.maxSlots
  }

  setMaxSlots(maxSlots: number): void {
    const next = Math.max(1, maxSlots)
    if (next === this.maxSlots) return
    this.maxSlots = next
    this.reconcile()
    this.emit()
  }

  isLive(id: ChatMapLiveSlotId): boolean {
    return this.liveById.has(id)
  }

  getLiveIds(): ReadonlyArray<ChatMapLiveSlotId> {
    return [...this.liveById.keys()]
  }

  /**
   * Report whether a card is near the viewport.
   * Becoming visible claims a live slot (newest wins; may evict the least-recent
   * live map, preferring off-screen keep-alives but falling back to on-screen).
   * Leaving the viewport keeps the map live until capacity is needed elsewhere.
   */
  setVisible(id: ChatMapLiveSlotId, isVisible: boolean): void {
    const wasVisible = this.visibleById.has(id)
    if (isVisible === wasVisible) return

    if (isVisible) {
      this.visibleById.set(id, { rank: this.nextRank++ })
      this.claim(id)
    } else {
      this.visibleById.delete(id)
      // Keep liveById — off-screen maps stay mounted until capacity is needed.
      // Promote any visible cards still waiting on a slot.
      this.promoteVisibleWaiters()
    }
    this.emit()
  }

  /** Drop a card entirely (unmount / slot id change). */
  unregister(id: ChatMapLiveSlotId): void {
    const hadVisible = this.visibleById.delete(id)
    const hadLive = this.liveById.delete(id)
    if (!hadVisible && !hadLive) return
    this.reconcile()
    this.emit()
  }

  subscribe(listener: ChatMapLiveSlotListener): () => void {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  private claim(id: ChatMapLiveSlotId): void {
    const visible = this.visibleById.get(id)
    if (!visible) return

    if (this.liveById.has(id)) {
      this.liveById.set(id, { rank: visible.rank })
      return
    }

    if (this.liveById.size < this.maxSlots) {
      this.liveById.set(id, { rank: visible.rank })
      return
    }

    const victimId = this.findEvictionCandidate()
    if (victimId) {
      this.liveById.delete(victimId)
    }
    this.liveById.set(id, { rank: visible.rank })
  }

  /**
   * Prefer evicting off-screen live maps so scrolling between cards can reclaim
   * capacity without tearing down the only on-screen map first.
   */
  private findEvictionCandidate(): ChatMapLiveSlotId | undefined {
    const offScreenVictim = this.findLowestRankLiveId({ preferOffScreen: true })
    if (offScreenVictim) return offScreenVictim
    return this.findLowestRankLiveId({ preferOffScreen: false })
  }

  private findLowestRankLiveId(options: { preferOffScreen: boolean }): ChatMapLiveSlotId | undefined {
    let victimId: ChatMapLiveSlotId | undefined
    let lowestRank = Number.POSITIVE_INFINITY

    this.liveById.forEach((entry, id) => {
      if (options.preferOffScreen && this.visibleById.has(id)) return
      if (entry.rank < lowestRank) {
        lowestRank = entry.rank
        victimId = id
      }
    })

    return victimId
  }

  /**
   * After capacity shrinks or a live card unregisters, drop excess live holders
   * (preferring off-screen victims) and promote visible waiters into free or
   * reclaimable (off-screen keep-alive) slots.
   */
  private reconcile(): void {
    while (this.liveById.size > this.maxSlots) {
      const victimId = this.findEvictionCandidate()
      if (!victimId) break
      this.liveById.delete(victimId)
    }

    this.promoteVisibleWaiters()
  }

  /**
   * Give live slots to the highest-ranked visible cards that are not yet live.
   * When at capacity, only reclaim off-screen keep-alive holders — never steal
   * from another still-visible map.
   */
  private promoteVisibleWaiters(): void {
    const waiting = [...this.visibleById.entries()]
      .filter(([id]) => !this.liveById.has(id))
      .sort((a, b) => b[1].rank - a[1].rank)

    for (const [id, entry] of waiting) {
      if (this.liveById.size < this.maxSlots) {
        this.liveById.set(id, { rank: entry.rank })
        continue
      }

      const offScreenVictim = this.findLowestRankLiveId({ preferOffScreen: true })
      if (!offScreenVictim) break
      this.liveById.delete(offScreenVictim)
      this.liveById.set(id, { rank: entry.rank })
    }
  }

  private emit(): void {
    this.listeners.forEach((listener) => {
      listener()
    })
  }
}

/** Prefetch buffer so maps can mount slightly before fully on-screen. */
export const CHAT_MAP_LIVE_SLOT_ROOT_MARGIN = '160px 0px'

export const CHAT_MAP_LIVE_SLOTS_MOBILE = 2
export const CHAT_MAP_LIVE_SLOTS_DESKTOP = 3
